import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Badge,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {
    certificateRegionScope,
    declaredCertificateGrant,
    declaredDnsGrant,
    hasCertificateDeclaration,
    ProviderConnection,
} from '../types';
import {
    capabilitySnapshotCaption,
    declarationExceedsLastProbe,
} from '../statusPresentation';

// FNP-1248 — WHAT THIS CREDENTIAL CAN DO.
//
// ⚠️ A READ-ONLY CREDENTIAL IS A WORKING CREDENTIAL. The single most important
// presentation decision in this component.
//
// The catalog says Route 53 canWriteRecords: true. A tenant who deliberately
// handed Flexi a READ-ONLY role has a connection that cannot write, and that is
// a correct, intentional, fully-functional setup — not a fault. Rendering those
// capabilities as red crosses, or as an "incomplete" state with a warning, tells
// a careful tenant that their careful choice is broken.
//
// So capabilities render as PRESENCE and ABSENCE ("can create zones" /
// "cannot create zones"), in neutral tones, with no remediation prompt. The only
// thing coloured red in this module is an actual failure.

/**
 * FNP-1399 — name the grants the tenant actually made.
 *
 * ⚠️ IT LISTS WHAT WAS GRANTED RATHER THAN SUMMARISING IT. The badge this
 * replaced said "This role can create and delete zones" whenever ANY write
 * grant existed, so a customer who granted only record writes read that Flexi
 * could delete their hosted zones. Saying less than the truth is safer than
 * saying more, but saying the wrong thing is worse than either.
 *
 * The caller only renders this when at least one grant is true, so the empty
 * case cannot be reached from there; it still returns the read-only wording
 * rather than an empty badge, because a badge with no text is a rendering bug
 * wearing a capability's clothes.
 */
const declaredGrantSummary = (
    records: boolean,
    create: boolean,
    remove: boolean,
): string => {
    const granted: string[] = [];
    if (records) {
        granted.push(I18n.get('change records'));
    }
    if (create) {
        granted.push(I18n.get('create zones'));
    }
    if (remove) {
        granted.push(I18n.get('delete zones'));
    }
    if (granted.length === 0) {
        return I18n.get('Read-only');
    }
    return `${I18n.get('This role can')} ${granted.join(', ')}`;
};

const CapabilityRow = (
    {
        label,
        granted,
        declaredNotProbed = false,
    }: {
        label: string,
        granted: boolean,
        // FNP-1445 — this row's grant was DECLARED after the probe that says No.
        declaredNotProbed?: boolean,
    },
) => (
    <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
        <Text size={'sm'}>{label}</Text>
        {/* ⚠️ FNP-1445 — A BARE `No` AGAINST A GRANT THE TENANT JUST TICKED IS
            A CONTRADICTION, NOT A FACT. The row reads the last probe, so it
            answers honestly for its own question and still lands underneath a
            badge asserting the opposite. Naming the row "not checked yet"
            keeps both statements true and says which one is out of date.

            ⚠️ 'blue' here, and still never 'red'. This is a stale reading, not
            a refusal — colouring it as failure would tell a tenant their new
            grant was rejected when nothing has looked at it yet. */}
        <Badge
            variant={'light'}
            color={declaredNotProbed ? 'blue' : (granted ? 'green' : 'gray')}
            size={'sm'}
        >
            {declaredNotProbed
                ? I18n.get('Not checked yet')
                : (granted ? I18n.get('Yes') : I18n.get('No'))}
        </Badge>
    </Group>
);

/**
 * FNP-1445 — WHEN the probe behind these rows ran.
 *
 * ⚠️ NEVER-VALIDATED AND VALIDATED-LONG-AGO ARE DIFFERENT STATES, and the rows
 * must not imply a check that never happened. `lastValidatedAt` is nullable on
 * a connection that has never been validated, so the caller renders the rows
 * with no time rather than inventing one — the same split the table column
 * makes for the same reason.
 */
const lastProbeLabel = (connection: ProviderConnection): string => {
    if (!connection.lastValidatedAt) {
        return I18n.get('Result of the last validation check');
    }
    const when = new Date(connection.lastValidatedAt);
    if (Number.isNaN(when.getTime())) {
        // A malformed timestamp renders as no timestamp. "Invalid Date" on a
        // reassurance panel is worse than saying nothing about when.
        return I18n.get('Result of the last validation check');
    }
    return `${I18n.get('Checked')} ${when.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })}`;
};

/**
 * FNP-1398 — where the certificate grant applies.
 *
 * ⚠️ AN EMPTY LIST IS "ANY REGION", NOT "NONE", and rendering it the obvious
 * way gets it exactly backwards. `provider-connection/src/model.ts:267` stores
 * an unconstrained connection as `supportedRegions: []`, so a `.length === 0`
 * branch reading "no regions supported" would tell a tenant their working grant
 * covers nothing — the least restricted state reported as the most restricted,
 * on the page they opened to be reassured.
 *
 * The tri-state comes from `certificateRegionScope()` rather than being
 * re-derived here, so the empty-means-any rule lives in one place and a second
 * reader cannot disagree with it.
 */
const CertificateRegions = (
    {connection}: { connection: ProviderConnection },
) => {
    const scope = certificateRegionScope(connection);

    if (scope.kind === 'UNKNOWN') {
        // Distinct from "any": the API said nothing about regions at all.
        return (
            <Text size={'xs'} c={'dimmed'} data-testid={'certificate-regions-unknown'}>
                {I18n.get('Regions not enumerated for this connection')}
            </Text>
        );
    }

    if (scope.kind === 'ANY') {
        return (
            <Text size={'xs'} c={'dimmed'} data-testid={'certificate-regions-any'}>
                {I18n.get('Not limited to particular regions')}
            </Text>
        );
    }

    return (
        <Group gap={4} data-testid={'certificate-regions-listed'}>
            {scope.regions.map(region => (
                <Badge
                    key={region}
                    variant={'outline'}
                    color={'gray'}
                    size={'sm'}
                >
                    {region}
                </Badge>
            ))}
        </Group>
    );
};

export const CapabilitySnapshot = (
    {connection}: { connection: ProviderConnection },
) => {

    const capabilities = connection.capabilities;

    // `capabilities` is non-null in the schema, but a partial/degraded response
    // must not throw inside a render.
    if (!capabilities) {
        return (
            <Text size={'sm'} c={'dimmed'}>
                {productCopy('{product} has no capability snapshot for this connection.')}
            </Text>
        );
    }

    const recordTypes = capabilities.supportedRecordTypes ?? [];

    // ⚠️ FNP-1233, split by FNP-1399 — the DECLARED grants, read through the
    // fail-closed helper. See types.ts: an absent block or field resolves to
    // NOT granted, never to granted-by-omission.
    //
    // ⚠️ THREE BADGES, NOT ONE. This used to be a single badge reading "This
    // role can create and delete zones", driven by one boolean that was the OR
    // of all three grants — so a customer who granted only record writes was
    // told, on their own connection page, that Flexi could delete their zones.
    // The whole point of storing the grants separately is that the product can
    // now say which ones it actually holds.
    const declaredRecords = declaredDnsGrant(connection, 'canWriteRecords');
    const declaredCreate = declaredDnsGrant(connection, 'canCreateZones');
    const declaredDelete = declaredDnsGrant(connection, 'canDeleteZones');
    const declaredAny = declaredRecords || declaredCreate || declaredDelete;

    // ⚠️ FNP-1445 — THE BADGE ABOVE AND THE ROWS BELOW READ DIFFERENT FACTS.
    // The badge is the tenant's DECLARATION; the rows are the last VALIDATION
    // probe. Declaring a grant does not re-probe, so the two can disagree on
    // screen with both genuinely fresh from the API. The shared predicate lives
    // in statusPresentation so the caption and these rows cannot drift apart.
    const staleProbe = declarationExceedsLastProbe(connection);

    return (
        <Stack gap={'sm'}>

            {/* ⚠️ The caption changes tense on a non-ACTIVE connection. A
                snapshot records what the credential proved AT CONNECT TIME; next
                to a broken status, presenting it as current fact would claim the
                connection can do things while nothing works at all. */}
            <Text size={'xs'} c={'dimmed'} fw={500}>
                {capabilitySnapshotCaption(connection)}
            </Text>

            {/* ─── FNP-1233: THE TENANT'S DECLARATION ───
                Shown first because it is what makes the rest meaningful: with
                writes undeclared, the write rows below are uniformly No, and a
                reader with no explanation would think the credential failed. */}
            <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
                <Text size={'sm'}>{I18n.get('Declared to write')}</Text>
                <Badge
                    variant={'light'}
                    color={declaredAny ? 'green' : 'gray'}
                    size={'sm'}
                    data-testid={'writable-declared'}
                >
                    {declaredAny
                        ? declaredGrantSummary(
                            declaredRecords,
                            declaredCreate,
                            declaredDelete,
                        )
                        : I18n.get('Read-only')}
                </Badge>
            </Group>

            {/* ─── FNP-1445: WHAT THE ROWS BELOW ACTUALLY ARE ───
                They are one probe's result, not a live reading. Saying so, with
                the date, is what lets a tenant tell a stale No from a real one
                instead of reading the block as a single contradictory claim. */}
            <Text size={'xs'} c={'dimmed'} data-testid={'last-probe-label'}>
                {lastProbeLabel(connection)}
            </Text>

            <CapabilityRow
                label={I18n.get('Write records')}
                granted={capabilities.canWriteRecords}
                declaredNotProbed={declaredRecords && !capabilities.canWriteRecords}
            />
            <CapabilityRow
                label={I18n.get('Create zones')}
                granted={capabilities.canCreateZones}
                declaredNotProbed={declaredCreate && !capabilities.canCreateZones}
            />
            <CapabilityRow
                label={I18n.get('Delete zones')}
                granted={capabilities.canDeleteZones}
                declaredNotProbed={declaredDelete && !capabilities.canDeleteZones}
            />
            {/* ⚠️ NO DECLARED COUNTERPART. Nameservers are probed but never
                declared, so this row has nothing that could overtake it. */}
            <CapabilityRow
                label={I18n.get('Set nameservers')}
                granted={capabilities.canSetNameservers}
            />

            {/* ⚠️ FNP-1445 — NAME THE REMEDY, ONCE, AND ONLY WHEN IT APPLIES.
                The tenant granted something in their AWS account that nothing
                has checked yet; Re-validate is the control that checks it. The
                rows above already carry the state, so this says what to do and
                stops. */}
            {staleProbe && (
                <Text size={'xs'} c={'dimmed'} data-testid={'declaration-ahead-of-probe'}>
                    {productCopy('You granted more than the last check found. Choose Re-validate so {product} can check again.')}
                </Text>
            )}

            {/* ─── FNP-1398: THE CERTIFICATE DECLARATION ───

                ⚠️ SHOWN EVEN WHEN NOTHING IS GRANTED, which is the point of the
                ticket. The panel used to omit this block entirely, so a tenant
                who ticked "Request TLS certificates" while connecting opened
                their own connection and found no mention of it — the page
                appearing to confirm they granted LESS than they did, at the
                exact moment a cautious customer goes looking for reassurance.

                ⚠️ SAME NEUTRAL TREATMENT AS THE DNS ROWS ABOVE. Grey for
                absence, never red, and no remediation prompt: a tenant who
                granted DNS and withheld certificates made a correct, supported
                choice, and this panel is not the place to argue with it. */}
            <Stack gap={4}>
                <Text size={'sm'} fw={500}>{I18n.get('Certificates')}</Text>

                {hasCertificateDeclaration(connection) ? (
                    <>
                        <CapabilityRow
                            label={I18n.get('Request certificates')}
                            granted={declaredCertificateGrant(connection, 'canRequest')}
                        />
                        <CapabilityRow
                            label={I18n.get('Delete certificates')}
                            granted={declaredCertificateGrant(connection, 'canDelete')}
                        />
                        <CertificateRegions connection={connection}/>
                    </>
                ) : (
                    // ⚠️ "NOT RECORDED" IS NOT "NOT GRANTED", and the ticket
                    // asks for these two to be tellable apart. A connection
                    // predating the block genuinely has no declaration on file;
                    // rendering that as two grey No badges would state a fact
                    // about the tenant's choice that nobody ever recorded.
                    <Text size={'xs'} c={'dimmed'} data-testid={'certificate-declaration-unknown'}>
                        {I18n.get('No certificate permissions were recorded for this connection.')}
                    </Text>
                )}
            </Stack>

            <Stack gap={4}>
                <Text size={'sm'}>{I18n.get('Record types')}</Text>
                {recordTypes.length > 0 ? (
                    <Group gap={4}>
                        {recordTypes.map(recordType => (
                            <Badge
                                key={recordType}
                                variant={'outline'}
                                color={'gray'}
                                size={'sm'}
                            >
                                {recordType}
                            </Badge>
                        ))}
                    </Group>
                ) : (
                    // Distinct from "none supported": an empty list here means
                    // the snapshot did not enumerate them.
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Not enumerated for this connection')}
                    </Text>
                )}
            </Stack>

        </Stack>
    );
};
